<template>
	<div class="title_drawer">
		<!-- 右侧抽屉容器 -->
		<el-drawer v-model="drawerVisible" placement="right" size="1200px" title="" class="cart-drawer" v-loading="loading">
			<template #header>
				<div class="flex items-center">
					<div class="title">{{ $t('uChoicePc.myshoppingCart') }}</div>
					<span class="ml-[16px] text-[#0D0421] text-sm">
						{{ $t('uChoicePc.totalProducts', { count: totalQuantity }) }}
					</span>
				</div>
			</template>
			<!-- 空购物车 -->
			<div v-if="!loading && !cartList.length" class="py-20">
				<el-empty :image="emptyIcon" :description="$t('uChoicePc.cartEmpty')">
					<template #default>
						<el-button type="danger" round @click="goShopping">
							{{ $t('uChoicePc.goShopping') }}
						</el-button>
					</template>
				</el-empty>
			</div>

			<!-- 购物车有商品 -->
			<template v-else>
				<!-- ElementPlus Table 商品列表 -->
				<el-table
					:data="cartList"
					:tree-props="treeProps"
					default-expand-all
					fit
					class="text-[14px]"
					size="large"
					header-align="left"
					:cell-style="{ padding: '12px 8px' }"
					:row-key="getCartRowKey"
				>
					<!-- 勾选框+商品信息 -->
					<el-table-column min-width="240" :label="$t('uChoicePc.productName')" header-align="left" class="first_col">
						<template #default="scope">
							<div class="flex items-center gap-3" style="flex: 1">
								<el-checkbox :model-value="scope.row.selected" size="large" @change="() => checkSingle(scope.row)" />
								<div v-if="scope.row.level === 1" class="flex">
									<!-- 门店名称 -->
									<span v-if="cartList.length > 1 || userStore.storeQuantity > 1" class="flex items-center">
										<img :src="storeGrayee" alt="" class="w-[24px]" />
										<span class="ml-[8px] text-4 text-[#6D6A75]">{{ scope.row.storeName?.value }}</span>
									</span>
									<span v-else>{{ $t('uChoicePc.selectAll') }}</span>
								</div>
								<div v-else class="flex items-center" style="flex: 1">
									<img :src="scope.row.mainImage" alt="" class="w-12 h-12 rounded object-cover" />
									<div class="ml-2 text-[#0D0421]" style="flex: 1">{{ scope.row.productName?.value }}</div>
								</div>
							</div>
						</template>
					</el-table-column>
					<!-- 规格 -->
					<el-table-column :label="$t('uChoicePc.specification')" min-width="200">
						<template #default="scope">
							<span class="mt-1" v-if="scope.row.level !== 1">{{ handleData(scope.row.specConfDto) }}</span>
						</template>
					</el-table-column>
					<!-- 数量 -->
					<el-table-column :label="$t('uChoicePc.quantity')" min-width="150" align="center">
						<template #default="scope">
							<el-input-number v-if="scope.row.level !== 1" v-model.number="scope.row.quantity" :min="1" class="w-28" @change="onChangeQuantity($event, scope.row)" />
						</template>
					</el-table-column>
					<!-- 单价 -->
					<el-table-column :label="$t('uChoicePc.unitPrice')" min-width="150" align="right">
						<template #default="scope">
							<div v-if="scope.row.level !== 1" class="flex justify-end">
								<span>{{ getFormatPrice(scope.row) }}</span>
								<span>/{{ scope.row.unitName?.value }}</span>
							</div>
						</template>
					</el-table-column>
					<!-- 小计 -->
					<el-table-column :label="$t('uChoicePc.subtotal')" min-width="140" align="right">
						<template #default="scope">
							<div class="text-[#F92863] font-medium flex justify-end">
								<span v-if="scope.row.level !== 1">{{ getFormatSumPrice(scope.row) }}</span>
							</div>
						</template>
					</el-table-column>
					<!-- 操作 -->
					<el-table-column :label="$t('uChoicePc.operation')" width="120">
						<template #default="scope">
							<span v-if="scope.row.level !== 1" @click="handleRemoveItem(scope.row)"> <img :src="profileDelBtn" class="w-[24px] cursor-pointer" /> </span>
						</template>
					</el-table-column>
				</el-table>
			</template>
			<!-- 底部结算栏 -->
			<template #footer>
				<div class="flex justify-between items-center bg-white rounded-xl border-t border-t-[#E8E8E8]">
					<div class="flex items-center">
						<el-button link @click="onClearCart">
							{{ $t('uChoicePc.DeleteTheSelectedItem') }}
						</el-button>
					</div>

					<div class="flex items-center">
						<div class="mr-10 text-right">
							<span class="text-[#0D0421] text-sm mr-2">{{ $t('uChoicePc.total') }}：</span>
							<span class="text-[#F92863] text-2xl font-bold">{{ priceSign }}{{ sumAllPrices.toFixed(2) }}</span>
						</div>
						<el-button type="danger" size="large" class="btn_item" @click="onCheckout">
							<span class="text-[18px] font-500">{{ $t('uChoicePc.checkout') }}</span>
						</el-button>
					</div>
				</div>
			</template>
		</el-drawer>
		<!-- 单个删除确认 -->
		<confirm-dialog
			ref="deleteConfirm"
			:title="$t('uChoicePc.deleteMessage')"
			:msg="$t('uChoicePc.deleteSingleProductTips', [currentData.productName?.value])"
			@confirm="confirmDeleteCart"
		></confirm-dialog>
		<!-- 多个删除确认 -->
		<confirm-dialog
			ref="deleteConfirmSelected"
			:title="$t('uChoicePc.deleteMessage')"
			:msg="$t('uChoicePc.deleteMutipleProductTips', [selectedCartItems ? selectedCartItems.length : 0])"
			@confirm="confirmDeleteSelectedCart"
		></confirm-dialog>
	</div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Delete } from '@element-plus/icons-vue'
import useStore from '@/store'
import confirmDialog from '@/views/mall/components/confirm-dialog/index.vue'
import { getShoppingCartList, delShoppingCartBatch, updateShoppingCartQuantity } from '@/api/mall/product'
import lang from '@/lang/index'
import useUserStore from '@/store/user'
import type { CartProductItem, CartStoreItem, SpecConfItem } from '@/views/mall/types'

const store = useUserStore()
const i18n = lang.global
const router = useRouter()
const { userStore } = useStore()
// 抽屉显示状态
const drawerVisible = ref(false)
const priceSign = computed(() => userStore?.currencyInfo?.sign ?? '₱')
const emptyIcon = new URL(`@/assets/img/empty_icon.png`, import.meta.url).href
const storeGrayee = new URL(`@/assets/img/store_grayee.png`, import.meta.url).href
const profileDelBtn = new URL(`@/assets/img/profile_del_btn.png`, import.meta.url).href
const open = () => {
	drawerVisible.value = true
	getList()
	userStore.getStoreQuantity()
}
// 跳转商城首页
const goShopping = () => {
	router.push('/mall/home')
	drawerVisible.value = false
}
// 购物车总数量
const totalQuantity = computed(() => {
	return cartList.value.reduce(
		(sum: number, store: CartStoreItem) => sum + (store.productList || []).reduce((s: number, product: CartProductItem) => s + (product.quantity || 0), 0),
		0
	)
})
const cartList = ref<CartStoreItem[]>([])
/**
 * 为树形表格生成唯一行标识：门店分组没有 id，必须使用 storeId。
 * 门店与商品分别添加前缀，避免两类 ID 相同或分组缺少 id 时复用错误的名称和子行。
 */
const getCartRowKey = (row: CartStoreItem | CartProductItem): string => {
	return row.level === 1 ? `store-${(row as CartStoreItem).storeId}` : `cart-${row.id}`
}
const loading = ref(true)
const treeProps = reactive({
	checkStrictly: false,
	children: 'productList'
})
// 获取列表
const getList = () => {
	loading.value = true
	getShoppingCartList()
		.then((res) => {
			if (res.code === 200) {
				let list = res.data || []
				for (let i = 0; i < list.length; i++) {
					list[i].selected = false
					list[i].level = 1
					for (let j = 0; j < list[i].productList.length; j++) {
						list[i].productList[j].selected = false
						list[i].productList[j].level = 2
					}
				}
				cartList.value = list
			}
		})
		.catch(() => {
			ElMessage.error(i18n.t('uChoicePc.networkError'))
		})
		.finally(() => {
			loading.value = false
		})
}

// 修改数量
const onChangeQuantity = (val: number | undefined, row: CartProductItem) => {
	if (val && val < 1) {
		row.quantity = 1
		handleSumPrices()
		return
	}
	updateShoppingCartQuantity({ id: row.id, quantity: val })
		.then((res) => {
			if (res.code === 200) {
				handleSumPrices()
				userStore.getCartQuantity()
			}
		})
		.catch(() => {
			ElMessage.error(i18n.t('uChoicePc.networkError'))
		})
}
// 价格格式化
const formatPrice = (data: CartProductItem) => {
	const currencyValue = userStore?.currencyInfo
	const sign = currencyValue?.sign ?? '₱'
	const currencyCode = currencyValue?.value ?? 'PHP'
	const rawValue = data?.priceMap?.[`1-${currencyCode}`]
	const price = typeof rawValue === 'number' && !isNaN(rawValue) ? rawValue : 0
	return { sign, price }
}

// 返回单价
const getFormatPrice = (data: CartProductItem) => {
	const { sign, price } = formatPrice(data)
	return `${sign}${price.toFixed(2)}`
}

// 返回小计
const getFormatSumPrice = (data: CartProductItem) => {
	const { sign, price } = formatPrice(data)
	return `${sign}${(price * data.quantity).toFixed(2)}`
}

// 处理规格回显
const handleData = (specConfDto: SpecConfItem[] | null) => {
	if (!specConfDto) return ''
	try {
		return specConfDto
			.flatMap((val: SpecConfItem) => val.productSpecDetailDTOList ?? [])
			.map((item: { name: { value: string }; value: string }) => `${item.name?.value}：${item.value}`)
			.join(' ')
	} catch {
		return ''
	}
}
// 单个勾选
const checkSingle = (val: CartStoreItem | CartProductItem) => {
	if (val.level === 1) {
		const storeRow = val as CartStoreItem
		const index = cartList.value.findIndex((item: CartStoreItem) => item.storeId === storeRow.storeId)
		cartList.value[index].selected = !storeRow.selected
		cartList.value[index].productList.map((item: CartProductItem) => {
			item.selected = cartList.value[index].selected
			return item
		})
	} else {
		const productRow = val as CartProductItem
		productRow.selected = !productRow.selected
	}
	getSelectData()
	handleSumPrices()
}

const sumAllPrices = ref(0)
const handleSumPrices = () => {
	let sumPrices = 0
	for (let i = 0; i < cartList.value.length; i++) {
		const productList = cartList.value[i].productList || []
		for (let j = 0; j < productList.length; j++) {
			const product = productList[j]
			if (product.selected) {
				const { price } = formatPrice(product)
				sumPrices += product.quantity * price
			}
		}
	}
	sumAllPrices.value = Math.round(sumPrices * 100) / 100
}

const currentData = ref<CartProductItem>({} as CartProductItem)
const deleteConfirm = ref()
// 删除单个商品
const handleRemoveItem = async (data: CartProductItem) => {
	currentData.value = data
	deleteConfirm.value.openDialog()
}
// 确认删除单个商品
const confirmDeleteCart = () => {
	delShoppingCartBatch([currentData.value.id])
		.then((res) => {
			if (res.code === 200) {
				ElMessage.success(i18n.t('uChoicePc.deleteSuccess'))
				getList()
				userStore.getCartQuantity()
			}
		})
		.catch(() => {
			ElMessage.error(i18n.t('uChoicePc.networkError'))
		})
}
const selectedCartItems = ref<CartProductItem[]>([])
const deleteConfirmSelected = ref()

const getSelectData = () => {
	let arr: CartProductItem[] = []
	for (let i = 0; i < cartList.value.length; i++) {
		for (let j = 0; j < cartList.value[i].productList.length; j++) {
			if (cartList.value[i].productList[j].selected) {
				arr.push(cartList.value[i].productList[j])
			}
		}
	}
	selectedCartItems.value = arr
	return arr
}

// 清空购物车
const onClearCart = async () => {
	let data = getSelectData()
	if (!data.length) return
	deleteConfirmSelected.value.openDialog()
}

// 确认删除
const confirmDeleteSelectedCart = () => {
	let data = getSelectData()
	const selectedIds = data.filter((item: CartProductItem) => item.selected).map((item: CartProductItem) => item.id)
	if (selectedIds.length === 0) {
		ElMessage.warning(i18n.t('uChoicePc.selectProductsFirst'))
		return
	}
	delShoppingCartBatch(selectedIds)
		.then((res) => {
			if (res.code === 200) {
				ElMessage.success(i18n.t('uChoicePc.deleteSuccess'))
				getList()
				userStore.getCartQuantity()
			}
		})
		.catch(() => {
			ElMessage.error(i18n.t('uChoicePc.networkError'))
		})
}

// 结算
const onCheckout = () => {
	if (selectedCartItems.value.length === 0) {
		ElMessage.warning(i18n.t('uChoicePc.selectProductsFirst'))
		return
	}

	// 校验是否同一门店
	const storeIds = new Set<string | number>()
	for (const store of cartList.value) {
		if (store.productList.some((p: CartProductItem) => p.selected)) {
			storeIds.add(store.storeId)
		}
	}
	if (storeIds.size > 1) {
		ElMessage.warning(i18n.t('uChoicePc.PleaseCheckTheGoodsStore'))
		return
	}

	// 校验是否同一取货方式
	const salesModes = new Set<string | undefined>()
	for (const item of selectedCartItems.value) {
		salesModes.add(item.salesMode?.value)
	}
	if (salesModes.size > 1) {
		ElMessage.warning(i18n.t('uChoicePc.PleaseCheckTheSameSalesMode'))
		return
	}

	store.setOrder(selectedCartItems.value)
	drawerVisible.value = false
	router.push({
		path: '/mall/checkoutOrder',
		query: {
			from: 'cart',
			storeId: String(storeIds.values().next().value ?? '')
		}
	})
}

defineExpose({
	open
})
</script>

<style scoped lang="scss">
.title {
	font-size: 20px;
	font-weight: 500;
	line-height: 30px;
	letter-spacing: normal;
	color: #0d0421;
}
/* 仅深度覆盖表格表头背景，其余全部UnoCSS */
.cart-drawer :deep(.el-table th) {
	background-color: #f7f8fa;
}
.first_col {
	:deep(.el-table td.el-table__cell div) {
		display: flex;
		align-items: center;
	}
}
:deep(.el-table .cell) {
	padding: 2px 16px 2px 24px;
}
:deep(.el-table__cell.is-right .cell) {
	text-align: right;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	padding-right: 0;
}
.btn_item {
	min-width: 200px;
	height: 54px;
	border-radius: 16px;
	background: #f92863;
}
:deep(.el-table th.el-table__cell.is-leaf),
:deep(.el-table td.el-table__cell) {
	border-bottom: none;
}
:deep(.el-table__inner-wrapper::before) {
	display: none;
}
:deep(.el-table__header) {
	border-bottom: 1px solid #e8e8e8;
}
</style>
